import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import { relativeTime } from "@/lib/format";

/**
 * Distribution panel — light channels overview and sync health.
 */
export default function DistributionPanel({ channels = [], reservations = [] }) {
    const { t } = useTranslation();
    const bySource = {};
    for (const r of reservations) {
        const s = r.source || "direct";
        bySource[s] = (bySource[s] || 0) + 1;
    }
    const totalReservations = reservations.length || 1;
    const anyIssue = channels.some((c) => !["connected", "active", "healthy"].includes(c.status));
    const lastSync = channels.map((c) => c.last_sync_at).filter(Boolean).sort().slice(-1)[0];

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="distribution-card">
            <div className="flex items-center justify-between px-5 pt-4">
                <h2 className="text-[15px] font-semibold text-foreground">
                    {t("dashboard.distribution")}
                </h2>
                {lastSync && (
                    <div className="text-[11.5px] text-muted-foreground">
                        {t("dashboard.last_sync")}: <span className="tabular-nums text-foreground/90">{relativeTime(lastSync)}</span>
                    </div>
                )}
            </div>

            <div className="px-5 py-4">
                {channels.length === 0 ? (
                    <div className="text-[12.5px] text-muted-foreground">
                        {t("dashboard.no_channels")}
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {channels.slice(0, 6).map((c) => {
                            const key = c.slug || (c.name || "").toLowerCase();
                            const count = bySource[key] || bySource[c.name] || 0;
                            const pct = Math.round((count / totalReservations) * 100);
                            const healthy = ["connected", "active", "healthy"].includes(c.status);
                            return (
                                <li key={c.id || c.name} className="text-[12.5px]">
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex h-1.5 w-1.5 rounded-full ${healthy ? "bg-emerald-500" : "bg-rose-500"}`} />
                                        <span className="flex-1 truncate text-foreground/90">
                                            {c.name || c.id}
                                        </span>
                                        <span className="tabular-nums text-muted-foreground/70">{count}</span>
                                    </div>
                                    <div className="mt-1 h-[3px] w-full overflow-hidden rounded-full bg-muted">
                                        <div
                                            className={`h-full ${healthy ? "bg-emerald-500/70" : "bg-rose-500/70"} transition-all`}
                                            style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="border-t border-border/60 px-5 py-3">
                {anyIssue ? (
                    <Link
                        to="/channels"
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-rose-700 hover:underline"
                        data-testid="distribution-issue-link"
                    >
                        <AlertTriangle size={13} />
                        {t("dashboard.att_channel_issues", { count: channels.filter((c) => !["connected", "active", "healthy"].includes(c.status)).length })}
                        <ChevronRight size={13} />
                    </Link>
                ) : channels.length > 0 ? (
                    <div className="inline-flex items-center gap-1.5 text-[12.5px] text-emerald-700">
                        <CheckCircle2 size={13} />
                        {t("dashboard.sync_ok")}
                    </div>
                ) : null}
            </div>
        </section>
    );
}
